import type { SvgIconTypeMap } from "@mui/material";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import PeopleIcon from "@mui/icons-material/People";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import VideoCameraBackIcon from "@mui/icons-material/VideoCameraBack";
import CastIcon from "@mui/icons-material/Cast";
import ChatIcon from "@mui/icons-material/Chat";
import ArticleIcon from "@mui/icons-material/Article";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import StyleIcon from "@mui/icons-material/Style";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import StoreIcon from "@mui/icons-material/Store";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import LinkIcon from "@mui/icons-material/Link";
import WidgetsIcon from "@mui/icons-material/Widgets";
import PollIcon from "@mui/icons-material/Poll";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CampaignIcon from "@mui/icons-material/Campaign";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import FlipCameraAndroidIcon from "@mui/icons-material/FlipCameraAndroid";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import VideoSettingsIcon from "@mui/icons-material/VideoSettings";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import InterestsIcon from "@mui/icons-material/Interests";
import ListIcon from "@mui/icons-material/List";
import WebIcon from "@mui/icons-material/Web";
import SettingsIcon from "@mui/icons-material/Settings";
import TuneIcon from "@mui/icons-material/Tune";
import PublicIcon from "@mui/icons-material/Public";
import InputIcon from "@mui/icons-material/Input";
import DownloadIcon from "@mui/icons-material/Download";
import GroupIcon from "@mui/icons-material/Group";
import ForumIcon from "@mui/icons-material/Forum";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import type { Action } from "@/partner/shared/lib";
import {
  OrganizationRoleType,
  PlatformFeatureType,
} from "@/types/graphql/partner";
import { PagePaths } from "./routes";

/**
 * Соответствие пунктов меню и прав (спека, «Меню и маршруты»). Пара
 * `action`/`subject` проверяется через `ability.can(action, subject)`; для
 * видео/трансляций (единственное место, где различие внутри одного
 * субъекта `Content` не выразить через CASL без экземпляра) используется
 * `roles` — прямая проверка `currentOrganizationRoles` (`hasRole`).
 */
export type MenuAccess =
  | { action: Action; subject: string }
  | { roles: OrganizationRoleType[] };

export interface MenuItemProps {
  textKey: string;
  to: string;
  icon?: OverridableComponent<SvgIconTypeMap> & {
    muiName: string;
  };
  access?: MenuAccess;
  /**
   * Пункт живёт только там, где у развёртывания включены эфиры
   * (`instance.liveEnabled`). На adult-инстансе такие пункты не
   * рендерятся, а их маршруты уводят на дашборд (app/router.tsx).
   */
  requiresLive?: boolean;
  /**
   * Пункт живёт только у организации с хотя бы одним контрактом (спека
   * §10): у остальных билинга по факту не бывает, пункт меню и раздел
   * прячутся вместе (см. `useHasBillingContracts`).
   */
  requiresBillingContracts?: boolean;
  /**
   * Пункт живёт только при фичефлаге платформы (включает админ): раздел
   * «Опросы» без PLAYER_POLLS не показывается, а маршрут уводит на
   * дашборд (app/router.tsx).
   */
  requiresPlatformFeature?: PlatformFeatureType;
  section?: never;
  items?: never;
}

export interface MenuSectionProps {
  textKey: string;
  section: string;
  items: MenuItemProps[];
  icon?: OverridableComponent<SvgIconTypeMap> & {
    muiName: string;
  };
  to?: never;
}

export const MenuItems: (MenuItemProps | MenuSectionProps)[] = [
  {
    to: PagePaths.partner,
    textKey: "menu.dashboard",
    icon: DashboardIcon,
    access: { action: "read", subject: "Analytics" },
  },
  {
    to: PagePaths.products,
    textKey: "menu.products",
    icon: ShoppingCartIcon,
    access: { action: "read", subject: "Product" },
  },
  {
    to: PagePaths.analytics,
    textKey: "menu.analytics",
    icon: EqualizerIcon,
    access: { action: "read", subject: "Analytics" },
  },
  {
    section: "audience",
    textKey: "menu.audience",
    icon: PeopleIcon,
    items: [
      {
        to: PagePaths.users,
        textKey: "menu.viewers",
        icon: PeopleIcon,
        // read User раздаётся всем непустым ролям (нужно для подписей,
        // task1-report.md), поэтому раздел зрителей гейтится ролями
        // напрямую, как videos/streams/collections, а не ability (e2e на
        // проде: роль videos не должна видеть «Пользователей»).
        access: {
          roles: [
            OrganizationRoleType.UsersType,
            OrganizationRoleType.AdminType,
          ],
        },
      },
      {
        to: PagePaths.audienceConversations,
        textKey: "menu.conversations",
        icon: ForumIcon,
        access: { action: "read", subject: "AssistantConversation" },
      },
      {
        to: PagePaths.audienceRequests,
        textKey: "menu.requests",
        icon: SupportAgentIcon,
        access: { action: "read", subject: "SupportRequest" },
      },
    ],
  },
  {
    section: "content",
    textKey: "menu.content",
    icon: PlayCircleFilledIcon,
    items: [
      {
        to: PagePaths.videos,
        textKey: "menu.videos",
        icon: VideoCameraBackIcon,
        // Видео и трансляции делят субъект Content, различаются условием
        // по type, которое CASL без экземпляра не проверяет — гейт по
        // ролям напрямую (см. shared/lib/ability.ts, hasRole).
        access: {
          roles: [
            OrganizationRoleType.VideosType,
            OrganizationRoleType.AdminType,
            OrganizationRoleType.AnalyticsType,
          ],
        },
      },
      {
        to: PagePaths.streams,
        textKey: "menu.streams",
        icon: CastIcon,
        requiresLive: true,
        access: {
          roles: [
            OrganizationRoleType.StreamsType,
            OrganizationRoleType.AdminType,
            OrganizationRoleType.AnalyticsType,
          ],
        },
      },
      {
        to: PagePaths.chat,
        textKey: "menu.chat",
        icon: ChatIcon,
        // Чат есть и под видео, и под стримами, поэтому доступ тот же, что
        // у обеих контентных ролей, без requiresLive.
        access: {
          roles: [
            OrganizationRoleType.VideosType,
            OrganizationRoleType.StreamsType,
            OrganizationRoleType.AdminType,
          ],
        },
      },
      {
        to: PagePaths.posts,
        textKey: "menu.posts",
        icon: ArticleIcon,
        access: { action: "read", subject: "Post" },
      },
      {
        to: PagePaths.collections,
        textKey: "menu.collections",
        icon: AllInboxIcon,
        // Коллекция — это Content с type: COLLECTION (отдельного субъекта
        // Collection на бэкенде нет, см. task1-report.md, сомнение 2),
        // право на неё выдаёт та же роль videos, что и на видео.
        access: {
          roles: [
            OrganizationRoleType.VideosType,
            OrganizationRoleType.AdminType,
            OrganizationRoleType.AnalyticsType,
          ],
        },
      },
      {
        to: PagePaths.tags,
        textKey: "menu.tags",
        icon: StyleIcon,
        access: { action: "read", subject: "Tag" },
      },
      {
        to: PagePaths.storage,
        textKey: "menu.storage",
        icon: StorageOutlinedIcon,
        access: { action: "read", subject: "Storage" },
      },
    ],
  },

  {
    section: "marketing",
    textKey: "menu.marketing",
    icon: StoreIcon,
    items: [
      {
        to: PagePaths.promocodes,
        textKey: "menu.promocodes",
        icon: CardGiftcardIcon,
        access: { action: "read", subject: "Promocode" },
      },
      {
        to: PagePaths.links,
        textKey: "menu.links",
        icon: LinkIcon,
        access: { action: "read", subject: "Link" },
      },
      {
        to: PagePaths.widgets,
        textKey: "menu.widgets",
        icon: WidgetsIcon,
        access: { action: "read", subject: "Widget" },
      },
      {
        to: PagePaths.polls,
        textKey: "menu.polls",
        icon: PollIcon,
        access: { action: "read", subject: "Poll" },
        requiresPlatformFeature: PlatformFeatureType.PlayerPollsType,
      },
      {
        to: PagePaths.broadcasts,
        textKey: "menu.broadcasts",
        icon: CampaignIcon,
        access: { action: "read", subject: "Automation" },
        requiresPlatformFeature: PlatformFeatureType.MarketingEmailsType,
      },
      {
        to: PagePaths.automations,
        textKey: "menu.automations",
        icon: AccountTreeIcon,
        access: { action: "read", subject: "Automation" },
        requiresPlatformFeature: PlatformFeatureType.MarketingEmailsType,
      },
      {
        to: PagePaths.emailTemplates,
        textKey: "menu.emailTemplates",
        icon: MailOutlineIcon,
        access: { action: "read", subject: "EmailTemplate" },
        requiresPlatformFeature: PlatformFeatureType.MarketingEmailsType,
      },
      {
        to: PagePaths.marketingContacts,
        textKey: "menu.marketingContacts",
        icon: ContactMailIcon,
        access: { action: "read", subject: "ContactList" },
        requiresPlatformFeature: PlatformFeatureType.MarketingEmailsType,
      },
    ],
  },

  {
    section: "finance",
    textKey: "menu.finance",
    icon: PointOfSaleIcon,
    items: [
      {
        to: PagePaths.payments,
        textKey: "menu.payments",
        icon: AccountBalanceWalletIcon,
        access: { action: "read", subject: "Payment" },
      },
      {
        // Счета по контракту и отчёты по доле с продаж (Task 11): тот же
        // способ гейта, что у пункта меню платежей зрителей, только
        // субъект ContractPayment (RevenueShareReport делит с ним право,
        // см. PartnerPolicy.ts).
        to: PagePaths.billing,
        textKey: "menu.billing",
        icon: ReceiptLongIcon,
        access: { action: "read", subject: "ContractPayment" },
        // Раздел скрыт целиком у организации без контрактов (спека §10,
        // финальное ревью P1): requiresBillingContracts.
        requiresBillingContracts: true,
      },
      {
        to: PagePaths.purchases,
        textKey: "menu.purchases",
        icon: ShoppingCartIcon,
        access: { action: "read", subject: "ProductPurchase" },
      },
      {
        // Task 7 (2026-08-01-purchases-cabinet-parity): Возвраты и Диспуты
        // объединены в один пункт меню — /partner/chargebacks с MUI Tabs
        // внутри (см. features/chargebacks/ui/Chargebacks.tsx). Второй
        // уровень вложенности меню не поддерживается MenuSectionProps
        // (items: MenuItemProps[], без вложенных секций), поэтому вариант
        // "родитель с двумя подпунктами" был не доступен без правки общей
        // инфраструктуры рендеринга меню — вне рамок этой задачи.
        to: PagePaths.chargebacks,
        textKey: "menu.chargebacks",
        icon: FlipCameraAndroidIcon,
        access: { action: "read", subject: "Refund" },
      },
      {
        to: PagePaths.subscriptions,
        textKey: "menu.subscriptions",
        icon: AutorenewIcon,
        access: { action: "read", subject: "Subscription" },
      },
    ],
  },

  {
    section: "appearance",
    textKey: "menu.appearance",
    icon: VideoSettingsIcon,
    items: [
      {
        to: PagePaths.platformBranding,
        textKey: "menu.platformBranding",
        icon: WallpaperIcon,
        access: { action: "update", subject: "Platform" },
      },
      {
        to: PagePaths.platformCustomCovers,
        textKey: "menu.platformCustomCovers",
        icon: MovieFilterIcon,
        access: { action: "read", subject: "PlatformCustomCover" },
      },
      {
        to: PagePaths.platformSocial,
        textKey: "menu.platformSocial",
        icon: InterestsIcon,
        access: { action: "update", subject: "Platform" },
      },
      {
        to: PagePaths.platformMenu,
        textKey: "menu.platformMenu",
        icon: ListIcon,
        access: { action: "read", subject: "PlatformMenuItem" },
      },
      {
        to: PagePaths.platformPages,
        textKey: "menu.platformPages",
        icon: WebIcon,
        access: { action: "read", subject: "PlatformPage" },
      },
    ],
  },

  {
    section: "settings",
    textKey: "menu.settings",
    icon: SettingsIcon,
    items: [
      {
        to: PagePaths.platformSettings,
        textKey: "menu.platformSettings",
        icon: TuneIcon,
        access: { action: "update", subject: "Platform" },
      },
      {
        to: PagePaths.countryAccesses,
        textKey: "menu.countryAccesses",
        icon: PublicIcon,
        access: { action: "read", subject: "CountryAccess" },
      },
      {
        to: PagePaths.streamEntrypoints,
        textKey: "menu.streamEntrypoints",
        icon: InputIcon,
        requiresLive: true,
        access: { action: "read", subject: "StreamEntrypoint" },
      },
      {
        to: PagePaths.team,
        textKey: "menu.team",
        icon: GroupIcon,
        access: { action: "manage", subject: "OrganizationUser" },
      },
      {
        to: PagePaths.reports,
        textKey: "menu.reports",
        icon: DownloadIcon,
        access: { action: "read", subject: "Report" },
      },
    ],
  },
];
